import React, { useEffect, memo, useMemo, useCallback } from 'react';
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import {
	getAGGCompliantData,
	type ChildQueryControllerBulkProps,
	type ChildQueryControllerProps,
} from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { LOAD_CHILDREN_EXPERIENCE } from '../../common/constants';
import {
	useListViewIssues as useListViewIssuesService,
	useListViewIssuesBulk as useListViewIssuesBulkService,
} from '../../services/get-list-view-issues';
import type { JWMListViewItems } from '../../services/get-list-view-issues/__generated_apollo__/JWMListViewItems';
import type {
	ListViewIssuesReturn,
	ListViewIssuesBulkReturn,
} from '../../services/get-list-view-issues/types';
import { useIssueFields } from '../get-issue-fields';
import messages from './messages';

type ListViewChildQueryControllerProps = ChildQueryControllerProps<ListViewIssuesReturn>;

export const ListViewChildQueryController = memo<ListViewChildQueryControllerProps>(
	({ parentHierarchyType, parentIssueId, onDataChange, parentHierarchyLevel }) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { jqlContext, quickFiltersJql, sorting, isHidingDoneItems } = useFilteredViewContextInput(
			{
				view: 'list',
			},
		);
		const {
			getQueryFieldIds,
			loading: isLoadingFields,
			isParentFieldVisible,
			isDevSummaryFieldVisible,
		} = useIssueFields();

		// Memoise the parent issue to avoid `useListViewIssuesService` creating
		// a new data object on every render
		const parentIssue = useMemo(
			() => ({
				hierarchyType: parentHierarchyType,
				hierarchyLevel: parentHierarchyLevel,
				issueId: parentIssueId,
			}),
			[parentHierarchyType, parentHierarchyLevel, parentIssueId],
		);

		const onComplete = useCallback(
			(responseData: JWMListViewItems) => {
				fireTrackAnalytics(createAnalyticsEvent({}), 'listChildrenData fetched', {
					totalChildren: getAGGCompliantData(responseData, 'list')?.edges?.length,
					parentIssueId,
					hierarchyLevel: parentHierarchyLevel,
				});
			},
			[createAnalyticsEvent, parentHierarchyLevel, parentIssueId],
		);

		const { data, error, hasNextPage, isLoadingNextPage, loading, loadNextPage } =
			useListViewIssuesService({
				input: {
					jqlContext,
					fieldIds: getQueryFieldIds(),
					quickFiltersJql,
					parentIssue: ff('jwm.list.remove-unnecessary-parent-issue-dependency')
						? {
								hierarchyType: parentHierarchyType,
								hierarchyLevel: parentHierarchyLevel,
								issueId: parentIssueId,
							}
						: parentIssue,
					sorting,
					isHidingDoneItems,
					isParentFieldVisible: isParentFieldVisible(),
					isDevSummaryFieldVisible: isDevSummaryFieldVisible(),
				},
				condition: !isLoadingFields,
				onComplete,
			});

		useQueryBasedViewAutomaticTracking(LOAD_CHILDREN_EXPERIENCE, {
			loading,
			error,
			shouldStartExperience: true,
		});

		useEffect(() => {
			onDataChange(parentIssueId, {
				data,
				error,
				hasNextPage,
				isLoadingNextPage,
				loading,
				loadNextPage,
			});
		}, [
			data,
			error,
			hasNextPage,
			isLoadingNextPage,
			loading,
			loadNextPage,
			onDataChange,
			parentIssueId,
		]);

		return error ? (
			<ErrorFlag error={error} title={messages.errorTitle} description={messages.errorMessage} />
		) : null;
	},
);

type ListViewChildQueryControllerBulkProps =
	ChildQueryControllerBulkProps<ListViewIssuesBulkReturn>;

/**
 * When this component used, it will make a request to get list of issues for a specific JQL query
 * Essentially it's acting as a proxy for some hooks, e.g. (`useIssueFields`)
 * It's a proxy written in component way so we can safely generate hook call as we go.
 *
 * When any result of the hooks changed, it will notify the callback using `onDataChange`.
 *
 * @deprecated use `ListViewChildQueryController` instead. Remove when cleaning up `jwm.remove-list-view-bulk-query`
 * */

export const ListViewChildQueryBulkController = memo<ListViewChildQueryControllerBulkProps>(
	({
		hasLoadingChildren,
		parentHierarchyType,
		parentIssueIds,
		onDataChange,
		parentHierarchyLevel,
		parentHierarchyLevels,
	}: ListViewChildQueryControllerBulkProps) => {
		const { jqlContext, quickFiltersJql, sorting, isHidingDoneItems } = useFilteredViewContextInput(
			{
				view: 'list',
			},
		);
		const {
			getQueryFieldIds,
			loading: isLoadingFields,
			isParentFieldVisible,
			isDevSummaryFieldVisible,
		} = useIssueFields();

		const {
			data,
			loading: isLoadingItems,
			error,
			isLoadingNextPage,
		} = useListViewIssuesBulkService({
			input: {
				hasLoadingChildren,
				jqlContext,
				fieldIds: getQueryFieldIds(),
				quickFiltersJql,
				parentIssues: parentIssueIds.map((issueId) => ({
					hierarchyType: parentHierarchyType,
					hierarchyLevel: parentHierarchyLevel,
					issueId,
				})),
				sorting,
				isHidingDoneItems,
				isParentFieldVisible: isParentFieldVisible(),
				isDevSummaryFieldVisible: isDevSummaryFieldVisible(),
			},
			condition: isLoadingFields === false && parentIssueIds.length > 0,
		});
		const loading = isLoadingFields || isLoadingItems;
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const totalIssues = Object.values(data).reduce((acc, curr) => acc + curr.length, 0);

		useQueryBasedViewAutomaticTracking(LOAD_CHILDREN_EXPERIENCE, {
			loading,
			error,
			shouldStartExperience: true,
		});

		useEffect(() => {
			/* This is a workaround from avoiding the analytics event fired twice on every fetch:
            First when items are loaded (loading: true -> false)
            Second when parentIssueIds are updated after items are updated(parentISsueIds: loadingparentIds -> expandedParentIds)
            Below !hasLoadingChildren check skips firing the event in second case 
           */
			if (loading || !hasLoadingChildren) {
				return;
			}

			fireTrackAnalytics(createAnalyticsEvent({}), 'listChildrenDataBulk fetched', {
				totalChildren: totalIssues,
				parentIssueIds,
				hierarchyLevels: parentHierarchyLevels,
			});
		}, [
			loading,
			createAnalyticsEvent,
			totalIssues,
			parentIssueIds,
			parentHierarchyLevels,
			hasLoadingChildren,
		]);

		useEffect(() => {
			onDataChange(parentIssueIds, {
				data,
				loading,
				error,
				isLoadingNextPage,
			});
		}, [data, error, loading, isLoadingNextPage, onDataChange, parentIssueIds]);

		return error ? (
			<ErrorFlag error={error} title={messages.errorTitle} description={messages.errorMessage} />
		) : null;
	},
);
